import $ from 'jquery';

import { doFetch } from './request.js';
import { cssAnimation2 } from '../util/animate-css.js';
import { displayDevMessages, displayEnvActionAlert, removeContainerDevMessages } from '../mpro/modules/mpro.error-handling.js';
import { mergeUrlParams } from '../util/url.js';
import { getFormDataForElement } from '../util/data.js';
import { isPlainObject } from '../util/types.js';

/**
 * @typedef { string | HTMLElement | JQuery | { [key: string]: (string | HTMLElement | JQuery) } } PartialDataFromInit
 *
 * @typedef {{
 *  type?: 'fragment' | 'modal-content' | 'drawer-content';
 *  url?: string | URL;
 *  method?: string;
 *  data?: RequestDataInit;
 *  dataFrom?: PartialDataFromInit;
 *  requestDataType?: RequestDataType;
 *  ref?: {};
 *  relatedTarget?: HTMLElement | JQuery<HTMLElement>;
 *  loader?: boolean;
 *  animation?: boolean | string;
 *  contentSelector?: string;
 *  strictContentSelector?: boolean;
 *  selectInnerContent?: boolean;
 *  resourceSelector?: string;
 *  replaceContainer?: boolean;
 *  emptyContainer?: boolean;
 *  restoreOriginal?: boolean;
 *  initCallback?: ($container: JQuery<HTMLElement>) => void;
 *  timeout?: number;
 *  throwOnInvalidStatus?: boolean;
 *  allowDetached?: boolean;
 * }} LoadPartialOptions
 *
 * @typedef {{
 *  requestId?: string;
 *  requestHandle?: string;
 *  success?: boolean;
 *  title?: string;
 *  content?: string;
 *  pageHtml?: { [key: string]: { html: string; mode?: 'replace' | 'append' | 'prepend'; animation?: boolean; } };
 *  footer?: string;
 *  message?: { format?: 'toast', type?: string; content: string; };
 *  reload?: string|boolean;
 *  formValues?: { value: any; id?: string; name?: string; selector?: any; append?: boolean; }[];
 *  modal?: import('../modules/components/modal.js').MpModalOptions;
 *  triggerEvents?: { event: string; bubbles?: boolean; cancelable?: boolean; selector?: string; data?: any; }[];
 *  location?: string;
 *  locationParams?: URLSearchParamsInit;
 *  extra?: string;
 * }} RemoteResponse
 *
 * @typedef {{
 *  url?: string|URL;
 *  response?: RemoteResponse;
 *  $context?: HTMLElement | JQuery<HTMLElement>;
 *  $relatedTarget?: HTMLElement | JQuery<HTMLElement>;
 * }} PartialData
 */

/**
 * Custom LoadPartialError error
 */
export class LoadPartialError extends Error {

	static NAME = 'LoadPartialError';

	/**
	 * @param {PartialContent} partial
	 * @param {string | Error} prevError
	 */
	constructor(partial, prevError) {
		// @ts-ignore
		super(prevError?.message ?? prevError);
		this.partial = partial;
		this.prevError = prevError;
		// eslint-disable-next-line unicorn/custom-error-definition
		this.name = LoadPartialError.NAME;
	}
}

/**
 * Partial content markup class
 */
export class PartialContent {

	/** @type {string} */
	#url;
	/** @type {string} */
	#location;
	/** @type {RemoteResponse} */
	#response;
	/** @type {JQuery<HTMLElement>} */
	#$context;
	/** @type {JQuery<HTMLElement>} */
	#$relatedTarget;
	/** @type {JQuery<HTMLElement>} */
	#$content;
	/** @type {JQuery<HTMLElement>} */
	#$originalContent;

	/**
	 * @param {Partial<PartialData>} data
	 */
	constructor(data) {
		const { url, response, $context, $relatedTarget } = data;
		this.#url = (url instanceof URL) ? url.toString() : url;
		// Use the setter to make sure the jQuery object is created
		this.$context = $context;
		this.$relatedTarget = $relatedTarget;
		// Use the response setter to make sure the location is processed
		this.response = response;
	}

	/**
	 * @param {string} url
	 * @param {URLSearchParamsInit} params
	 */
	processUrl(url, params) {

		if (url === 'self') {
			url = window.location.pathname + window.location.search;
		}

		if (url && params) {
			url = mergeUrlParams(url, params);
		}

		return url;
	}

	get url() {
		return this.#url;
	}

	get response() {
		return this.#response;
	}

	set response(response) {
		const resp = response || {};
		this.#response = resp;
		this.#location = this.processUrl(resp.location, resp.locationParams);
	}

	/** @returns {JQuery} */
	get $context() {
		return this.#$context;
	}

	/** @param {HTMLElement | JQuery<HTMLElement>} $context */
	set $context($context) {
		// @ts-ignore
		this.#$context = $context ? ($context.jquery ? $context : $($context)) : undefined;
	}

	/** @returns {JQuery<HTMLElement>} */
	get $relatedTarget() {
		return this.#$relatedTarget;
	}

	/** @param {HTMLElement | JQuery<HTMLElement>} $relatedTarget */
	set $relatedTarget($relatedTarget) {
		// @ts-ignore
		this.#$relatedTarget = $relatedTarget ? ($relatedTarget.jquery ? $relatedTarget : $($relatedTarget)) : undefined;
	}

	get $content() {
		return this.#$content;
	}

	set $content($content) {
		if ($content && !$content.jquery) {
			throw new Error('Content must be a jQuery object');
		}
		this.#$content = $content;
	}

	get $original() {
		return this.#$originalContent;
	}

	/** @param {JQuery<HTMLElement | Document | Text | Comment>} $content */
	set $original($content) {
		if ($content && !$content.jquery) {
			throw new Error('Content must be a jQuery object');
		}
		// @ts-ignore
		this.#$originalContent = $content;
	}

	get success() {
		return this.#response.success;
	}

	get title() {
		return this.#response.title;
	}

	set title(title) {
		this.#response.title = title;
	}

	get footer() {
		return this.#response.footer;
	}

	set footer(footer) {
		this.#response.footer = footer;
	}

	get content() {
		return this.#response.content;
	}

	set content(content) {
		this.#response.content = content;
	}

	get deleteContent() {
		return this.#response.content === '';
	}

	set deleteContent(content) {
		if (content === true) {
			this.#response.content = '';
		}
	}

	get location() {
		return this.#location;
	}

	set location(url) {
		this.#location = url;
	}

	get message() {
		let { message, success } = this.#response;

		message = (typeof message === 'string') ? {
			type: (success === true) ? 'success' : ((success === false) ? 'error' : 'warning'),
			content: message,
		} : message;

		return message;
	}

	set message(message) {
		this.#response.message = message;
	}

	get reload() {
		return this.#response.reload;
	}

	set reload(selector) {
		this.#response.reload = selector;
	}

	get formValues() {
		const { formValues } = this.#response;
		return (Array.isArray(formValues) && formValues.length > 0) ? formValues : undefined;
	}

	set formValues(formValues) {
		this.#response.formValues = formValues;
	}

	get pageHtml() {
		return this.#response.pageHtml;
	}

	set pageHtml(pageHtml) {
		this.#response.pageHtml = pageHtml;
	}

	get modal() {
		return this.#response.modal;
	}

	set modal(modal) {
		this.#response.modal = modal;
	}

	get triggerEvents() {
		return this.#response.triggerEvents;
	}

	set triggerEvents(triggerEvents) {
		this.#response.triggerEvents = triggerEvents;
	}

	get extra() {
		return this.#response.extra;
	}

	set extra(extra) {
		this.#response.extra = extra;
	}

	get requestId() {
		return this.#response.requestId;
	}

	set requestId(requestId) {
		this.#response.requestId = requestId;
	}

	get requestHandle() {
		return this.#response.requestHandle;
	}

	set requestHandle(requestHandle) {
		this.#response.requestHandle = requestHandle;
	}

	/**
	 * Check if the partial contains the 'content' property
	 */
	isContentful() {
		return Object.hasOwn(this.#response, 'content');
	}
}

export const resourceFilterSelector = '[data-ajax-content-resources]';

/**
 * @param {JQuery} $content
 * @param {string=} resourceSelector
 */
export const getAjaxResources = ($content, resourceSelector) => {

	let $contentResources;

	// Get the excluded content
	if (resourceSelector) {

		$contentResources = $content.filter(resourceSelector);
		$contentResources = $contentResources.add($content.find(resourceSelector));
		$contentResources = $contentResources.filter(function() {
			return ($(this).children().length > 0);
		});
	}
	else {
		$contentResources = $();
	}

	return $contentResources;
};

/**
 * @param {JQuery} $content
 * @param {string=} resourceSelector
 */
export const filterAjaxResources = ($content, resourceSelector = resourceFilterSelector) => {

	let $resContent = getAjaxResources($content, resourceSelector);
	let $resTop;
	let $resBottom;

	// Append resources to dom
	if ($resContent.length > 0) {

		// Avoid adding duplicate js/css to DOM
		$resContent = dropDuplicateResources($resContent);
		// Head resources
		$resTop = $resContent.filter('[data-ajax-placement-top]').detach();
		// Body resources
		$resBottom = $resContent.filter('[data-ajax-placement-bottom]').detach();
		// Content resources
		$resContent = $resContent.not($resTop).not($resBottom);
	}
	else {
		$resTop = $();
		$resBottom = $();
	}

	return { $resTop, $resBottom, $resContent };
};

/**
 * @param {JQuery} $content
 * @param {{ script?: boolean; link?: boolean; }=} tags
 */
export const dropDuplicateResources = ($content, tags) => {

	if (!tags) {
		tags = { script: true, link: true };
	}

	// Filter css resources that have already been loaded into DOM
	if (tags.link === true) {

		// Get all global link resources with href
		const $allLinks = $('link[href]'); // or document.styleSheets

		// Remove resources that were already loaded
		const $res = $content.filter('link[href]').add($content.find('link[href]')).filter(function() {
			// Note: getAttribute('href') will return the href as is authored, this.href will return the normalized href
			// Note: Use the eval'ed href prop so that both values are normalized
			// @ts-ignore
			return ($allLinks.filter((i, v) => v.href === this.href).length > 0);
		}).remove();

		// Remove the filtered resources from the content
		$content = $content.not($res);
	}

	// Filter js resources that have already been loaded into DOM
	if (tags.script === true) {

		// Get all global script resources with src
		const $allScripts = $('script[src]'); // or document.scripts

		// Remove resources that were already loaded
		const $res = $content.filter('script[src]').add($content.find('script[src]')).filter(function() {
			// Note: getAttribute('src') will return the src as is authored, this.src will return the normalized href
			// Note: Use the eval'ed src prop so that both values are normalized
			// @ts-ignore
			return ($allScripts.filter((i, v) => v.src === this.src).length > 0);
		}).remove();

		// Remove the filtered resources from the content
		$content = $content.not($res);
	}

	return $content;
};

/**
 * Load content that can be used to displaying in modals/drawers/tooltips etc
 *
 * @param {JQuery} $remoteContainer
 * @param {LoadPartialOptions=} options
 *
 * @returns {Promise<PartialContent>}
 */
export const loadPartial = async ($remoteContainer, options = {}) => {

	let $remoteContainerAll = $remoteContainer;
	let $remoteContainerExtra;

	if ($remoteContainer.length > 1) {
		$remoteContainerExtra = $remoteContainer.slice(1);
		$remoteContainer = $remoteContainer.first();
	}

	// Note: remote-url is deprecated
	let remoteData = $remoteContainer.data();
	if (!remoteData) {
		remoteData = {};
		$remoteContainer.data(remoteData);
	}

	// The remote container is already busy processing another loadPartial request
	if (remoteData._isLoadingPartial === true) {
		throw new Error('LoadPartialInProgress: The remote container is already busy processing another loadPartial request');
	}
	// Container is not in DOM
	else if (($remoteContainer[0].isConnected === false) && (options.allowDetached !== true)) {
		throw new Error('LoadPartialNotConnected: The remote container is not in DOM');
	}

	const {
		// ? Note: remote-url is deprecated
		url = (remoteData.url || remoteData.remoteUrl),
		type = 'fragment',
		loader = true,
		method = 'GET',
		dataFrom,
		requestDataType,
		ref,
		relatedTarget,
		timeout,
		throwOnInvalidStatus,
	} = options;

	// Data will be altered if dataFrom is set
	let { data } = options;

	// No url provided
	if (!url) {
		throw new Error('No url provided for loadPartial');
	}

	// Get data from another element
	if (dataFrom) {
		// Send only some keys
		if (isPlainObject(dataFrom)) {

			// Reset the data object
			data = new FormData();

			for (const [name, selector] of Object.entries(dataFrom)) {
				getFormDataForElement(selector, name, data);
			}
		}
		// Replace the whole data object
		else {
			/** @type {JQuery} */ // @ts-ignore
			const $element = $(dataFrom);
			data = getFormDataForElement($element);
		}
	}

	let $original;
	let $resTop;
	let $resBottom;
	let $resContent;
	let $content;
	/** @type {JQuery<HTMLElement>} */
	let $devMessagesFragment;

	// Create a new partial content object
	const partial = new PartialContent({ url, $context: $remoteContainerAll, $relatedTarget: relatedTarget });

	// Add loading animation
	if (loader === true) {
		mpro.addContentLoader($remoteContainer, 'auto');

		// Add an invisible loader to the extra container
		if ($remoteContainerExtra && $remoteContainerExtra.length > 0) {
			mpro.addContentLoader($remoteContainerExtra, ['none']);
		}
	}

	try {

		// Add the loading flag
		remoteData._isLoadingPartial = true;

		/** @type {RemoteResponse} */
		const jsonResponse = partial.response = await doFetch(url, {
			headers: {
				'X-Remote-Signature': type,
			},
			method,
			data,
			requestDataType,
			timeout,
		}, {
			// To provide access to AbortController.abort() method through ref.abort()
			ref,
		});

		// Don't process the response further if no 'content' property is set
		if (partial.isContentful() === false) {
			return partial;
		}

		// ----------------------------------------------------------------
		// The 'content' property is set, so process the response further
		// ----------------------------------------------------------------

		const {
			// Note: Not a good idea to set the default contentSelector, because the intention is to place the *whole* content in place (even if there are multiple fragments in it)
			contentSelector /* = '[data-ajax-main-content]' */,
			strictContentSelector = false,
			selectInnerContent = false,
			resourceSelector = resourceFilterSelector,
		} = options;

		// Parse the content
		$content = $(jsonResponse.content);
		let foundFilteredContent = false;

		// Always look for dev messages
		// @ts-ignore
		$devMessagesFragment = removeContainerDevMessages($content);
		$content = $content.not($devMessagesFragment);

		// Filter content
		if (contentSelector) {

			// /** @type {JQuery<HTMLElement | Text | Comment | Document>} */
			/** @type {JQuery<HTMLElement>} */
			let $filteredContent = $content.is(contentSelector) ? $content : $content.find(contentSelector);

			if ($filteredContent.length > 0) {

				// No need to look for resources on the whole content
				foundFilteredContent = true;

				// Select only the inner content
				if (selectInnerContent === true) {
					// @ts-ignore - Type 'JQuery<HTMLElement | Text | Comment | Document>' is not assignable to type 'JQuery<HTMLElement>'.ts(2322)
					$filteredContent = $filteredContent.contents();
				}

				// Find extra resources that are left out
				if (resourceSelector) {

					// Make sure the selected content is not in this list
					$filteredContent.detach();

					// Filter out-of-scope scripts/css
					({
						$resTop,
						$resBottom,
						$resContent,
					} = filterAjaxResources($content, resourceSelector));

					// Add remaining resources to the content
					if ($resContent.length > 0) {
						$filteredContent = $resContent.add($filteredContent);
					}
				}
				// Filter css/js resources that have already been loaded into DOM
				else {
					$filteredContent = dropDuplicateResources($filteredContent);
				}

				$content = $filteredContent;
			}
			// When no content is found, make sure it reflects that
			else if (strictContentSelector === true) {
				// The content is empty
				$content = $filteredContent;
			}
			else {
				// Modals/Drawers can often have no filtered content, so we don't want to display a warning
				// console.warn(`No content found for selector: ${contentSelector} in partial content from: ${url}`);
			}
		}

		// Must find and filter ajax resources
		if (foundFilteredContent === false) {

			// Filter out-of-scope scripts/css
			if (resourceSelector) {
				({
					$resTop,
					$resBottom,
				} = filterAjaxResources($content, resourceSelector));
			}
			// Filter css/js resources that have already been loaded into DOM
			else {
				$content = dropDuplicateResources($content);
			}
		}
	}
	catch (/** @type {any} */ error) {

		// Rethrow the error without sugar coating it
		if (throwOnInvalidStatus === true) {
			throw error;
		}

		console.warn('Partial content load failed', error);

		// Display error message in a bigger toast if in dev mode
		// Exclude AbortError
		if (/aborted/.test(error?.message ?? '') === false) {
			displayEnvActionAlert(error, {
				action: {
					type,
					url,
					method,
				},
			});
		}

		// Rethrow the error
		throw new LoadPartialError(partial, error);
	}
	// Cleanup
	finally {
		// Remove loading animation
		if (loader === true) {
			mpro.removeContentLoader($remoteContainer);

			// Remove the invisible loader from the extra container
			if ($remoteContainerExtra && $remoteContainerExtra.length > 0) {
				mpro.removeContentLoader($remoteContainerExtra);
			}
		}

		// Remove the loading flag
		remoteData._isLoadingPartial = false;
	}

	// Make sure mpro.push adds callbacks to queue
	mpro.disableCallbacks();

	const {
		replaceContainer = false,
		emptyContainer = false,
		restoreOriginal = false,
		animation = true,
		initCallback,
	} = options;

	// Replace the remoteContainer with new content
	if (replaceContainer === true) {

		// Got content back
		if ($content && $content.length > 0) {

			// Save the remote container reference as the wrapper
			if (restoreOriginal === true) {
				$original = $remoteContainerAll;
			}

			// Add a virtual wrapper to the content as it might not be a single element
			$content.wrapAll('<div/>');
			// Do the same for the original content, for the same reason as above
			$remoteContainerAll.wrapAll('<div/>');

			// Set the remote container as the div wrapper
			const $wrappedContent = $content.parent();
			const $wrappedRemoteContainerAll = $remoteContainerAll.parent();

			// Now we can append to DOM, but we must do it before unwrapping the content because the replaceWith function will be called on each child the contents returns (so there will be duplicates)
			// ? Note: must wrap inside a try-catch block because there might be errors inside destructors
			try {
				$wrappedRemoteContainerAll.replaceWith($wrappedContent);
			}
			catch (/** @type {any} */ error) {
				console.warn('Unexpected error when replacing remote container with new content', error);

				displayDevMessages(`<pre>Error: ${error.stack}\n\nCheck to console for more details.</pre>`, {
					type: 'error',
					title: 'Unexpected error when replacing remote container with new content',
				});
			}

			// Remove the div wrapper only after calling replaceWith
			$remoteContainerAll.unwrap('div');
			$content.unwrap('div');

			if (restoreOriginal === true) {
				// Detach so we keep event handlers
				$remoteContainerAll.detach();
			}
			else {
				$remoteContainerAll.remove();
			}

			// This is now the $remoteContainer
			$remoteContainerAll = $content;
			partial.$context = $content;

			// Take into account the possibility of multiple elements
			if ($content.length > 1) {
				// eslint-disable-next-line require-atomic-updates
				$remoteContainer = $content.first();
				// eslint-disable-next-line require-atomic-updates
				$remoteContainerExtra = $content.slice(1);
			}
		}
		// No content - empty the remote container
		else if (partial.deleteContent === true) {

			// Save the original contents
			if (restoreOriginal === true) {
				// Keep the original remote container reference
				$original = $remoteContainerAll;

				// Remove container with animation
				if (animation === true) {
					$original.fadeOut(200, () => {
						$original.detach();
					});
				}
				else {
					$original.detach();
				}
			}
			else {
				// Remove container with animation
				if (animation === true) {
					const $temp = $remoteContainerAll;
					$temp.fadeOut(200, () => {
						// Cleanup the remoteContainer
						$temp.empty().remove();
					});
				}
				else {
					// Cleanup the remoteContainer
					$remoteContainerAll.empty().remove();
				}
			}

			// To prevent animations and other stuff, reset references to the remoteContainer
			// eslint-disable-next-line require-atomic-updates
			$remoteContainerAll = $remoteContainer = $remoteContainerExtra = $();
		}
	}
	// Add/Append new content inside the remoteContainer
	else {

		// Save the original contents
		if (restoreOriginal === true) {
			$original = $remoteContainerAll.contents();
		}

		// Empty the remoteContainer (even if there is no content)
		if (emptyContainer === true) {
			if ($original?.length > 0) {
				// Detach so we keep event handlers
				$original.detach();
			}
			else {
				$remoteContainerAll.empty();
			}
		}

		// Got content back
		if ($content?.length > 0) {
			// * Append the content to ALL remote containers (this is different from replaceContainer)
			// ? Note: maybe add an option to decide whether to append to all or just the first one?
			$remoteContainerAll.append($content);
		}
	}

	// * Wrap the content (not the $remoteContainer because of append) in div so the callbacks can trigger properly
	if ($content?.length > 0) {

		const $context = $content.wrapAll('<div/>').parent();

		// Enhance the content with js bindings
		mpro.initStatics($context);

		// Additional enhance callback provided
		if (initCallback) {
			initCallback($context);
		}
		$content.unwrap('div');
	}

	// Call all queued callbacks and disable the queue
	mpro.loadPendingCallbacks();

	// Add head resources first
	if ($resTop && ($resTop.length > 0)) {
		mpro.get$B().prepend($resTop.children());
		$resTop = null;
	}

	// Add body resources last
	if ($resBottom && ($resBottom.length > 0)) {
		mpro.get$B().append($resBottom.children());
		$resBottom = null;
	}

	// Display dev messages
	if ($devMessagesFragment.length > 0) {
		// @ts-ignore
		displayDevMessages($devMessagesFragment.contents(), {
			title: `Dev messages from the last remote fragment (${url})`,
			preventNavigation: false,
		});
	}

	// Animate the new content
	if ((animation === true) && ($content?.length > 0)) {
		const animationModifiers = (animation === true) ? 'fadeIn' : animation;

		// Animate only new content
		if (emptyContainer === false) {
			for (const elem of $content) {
				if (elem instanceof HTMLElement) {
					cssAnimation2(elem, animationModifiers, 'faster');
				}
			}
		}
		// Animate the whole container
		else {
			// Animate only the first child
			cssAnimation2($content.filter((_, node) => node instanceof Element).first()[0], animationModifiers, 'faster');
		}
	}

	// Add handler to restore original contents
	if ((restoreOriginal === true) && ($original?.length > 0) && ($content?.length > 0)) {

		// TODO: find a way to restore the original when there is no content returned
		$content.find('[data-restore-original]').one('click.restore-original', (e) => {

			// Restore the remoteContainer
			if (replaceContainer === true) {

				// ? Note: the position of multiple elements inside the $original collection is lost
				$remoteContainer.replaceWith($original);
			}
			// Restore the contents from inside the container
			else {

				// Append the content
				$remoteContainer.empty().append($original);
			}

			// Disable all other handlers
			return false;
		});
	}

	// Add content to partial
	partial.$content = $content;
	// Add the original (replaced) content
	partial.$original = $original;

	return partial;
};
